'use client'

import { memo, Suspense, useContext, useEffect, useState } from 'react'
import Link from 'next/link'
import ExpandButton from '@dustin-web/microsite-components/src/expand-button'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { Skeleton } from '@dustin-web/microsite-components/src/skeleton'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'
import {
  useAccordionScroll,
  openAccordionItem,
} from '@dustin-web/microsite-components/src/accordion-group'
import type { Product } from '@productsite-beta/src/graphql/generated/graphql-types'
import {
  ACCORDION_AB_TEST_FLAG,
  SHOW_MORE_RECOMMENDED_PRODUCTS_FLAG,
} from '../../../product-page.constants'
import { AttachedSalesContext } from '../../../product-context/attached-sales-provider'
import ProductCard from './product-card/product-card'

import './product-add-ons.scss'

const ITEM_VALUE = 'accessories'

type Props = {
  mainProductTicket: string
  isMobile: boolean
  pathName: string
  chargerErpIdentifier?: string
}

const ProductAddOns = ({
  mainProductTicket,
  isMobile,
  pathName,
  chargerErpIdentifier,
}: Props) => {
  const [showAll, setshowAll] = useState(false)
  const { translate } = useTranslations()
  const { scrollToItem } = useAccordionScroll()
  const { list, handleIsSelected } = useContext(AttachedSalesContext)
  const { enabled: showMoreRecommendedProducts } = useFeatureFlag(
    SHOW_MORE_RECOMMENDED_PRODUCTS_FLAG
  )
  const { enabled: accordionsEnabled } = useFeatureFlag(ACCORDION_AB_TEST_FLAG)

  let initalProductCount = isMobile ? 1 : 2

  if (showMoreRecommendedProducts) {
    initalProductCount = isMobile ? 2 : 3
  }

  // Exclude the recommended common charger from the add-ons list so it is not
  // rendered as a duplicate of the dedicated ProductCommonCharger card.
  // The charger is still part of the attached-sales list provider logic.
  const visibleList = chargerErpIdentifier
    ? list.filter(
        item => item.product.productErpIdentifier !== chargerErpIdentifier
      )
    : list

  const products = showAll ? list : list.slice(0, initalProductCount)
  const viewAllAccessoriesLink = `${pathName}?tab=${ITEM_VALUE}&scrollToTab=true`

  //This logic recenters the mobile view onto the list because the layout uses a grid.
  //If the list's position in the layout changes, this code must be re-evaluated.
  useEffect(() => {
    if (!isMobile || !showAll) {
      return
    }

    const frame = requestAnimationFrame(() => {
      document.getElementById('addons-header')?.scrollIntoView({
        behavior: 'instant',
        block: 'start',
      })
    })

    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll])

  const toggleShowAll = () => {
    setshowAll(state => !state)
  }

  const handleAccordionClick = () => {
    openAccordionItem(ITEM_VALUE)
    scrollToItem(ITEM_VALUE)
  }

  return (
    <div className="product-add-ons">
      <div id="addons-header" className="header">
        <h2 className="h4">{translate('Product_CrossSell_AttachedSales_Header')}</h2>
        {accordionsEnabled ? (
          <button
            type="button"
            className="button-stripped"
            onClick={handleAccordionClick}
            data-testid="accessories-view-all-link">
            {translate('Product_CrossSell_AttachedSales_View_All')}
          </button>
        ) : (
          <Link
            data-testid="accessories-view-all-link"
            scroll={false}
            href={viewAllAccessoriesLink}>
            {translate('Product_CrossSell_AttachedSales_View_All')}
          </Link>
        )}
      </div>
      <div className="products-wrapper">
        <Suspense
          fallback={products.map((_, index) => {
            return <Skeleton key={index} height={isMobile ? 116 : 69} />
          })}>
          {products?.map((productData, key) => (
            <ProductCard
              key={key}
              digitalIndex={key}
              handleCheckbox={() => handleIsSelected(productData.product.productErpIdentifier)}
              isSelected={productData.isSelected}
              product={productData.product as Product}
              isSmallScreen={isMobile}
              ticket={mainProductTicket}
              digitalDataOrigin="productPageAttach"
            />
          ))}
        </Suspense>
      </div>
      {visibleList?.length > initalProductCount && (
        <ExpandButton
          className="u-pt-12"
          onClick={toggleShowAll}
          collapsedTitle={translate('ShowMore')}
          expandedTitle={translate('ShowLess')}
          expanded={showAll}
          ariaControls="expandable-add-ons-cards"
        />
      )}
    </div>
  )
}

export default memo(ProductAddOns)
